import React, { useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AxiosStatus } from 'api/axiosConfig';
import Button, { Themes } from 'ui/Button/Button';
import Toast from 'ui/Toast';
import { IApiResponse, Signatory } from 'utils/models';
import ModalWindow from '../ModalWindow/ModalWindow';
import { IStatusModalProps } from '../IModalProps';

import styles from './StatusModal.module.css';

import cross from 'assets/icons/fail.png';
import check from 'assets/icons/Group.png';

const StatusModal = ({
  open,
  onClose,
  children,
  status,
  onSuccess,
  onRetry,
  onRetryText,
  getReceipt,
  role,
  statusText,
}: IStatusModalProps) => {
  const [downloadState, setDownloadState] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [errorMessage, setErrorMessage] = useState('');

  const hideToast = () => setDownloadState(AxiosStatus.DEFAULT);

  const statusMap = {
    success: {
      icon: check,
      secondaryBtnText: 'Receipt',
      primaryBtnText: statusText ?? 'Continue',
    },
    failure: {
      icon: cross,
      secondaryBtnText: null,
      primaryBtnText: onRetryText || 'Retry',
    },
  };

  if (!open) {
    return null;
  }

  const renderSecondaryButton = () => {
    if (status !== 'success' || typeof getReceipt !== 'function') {
      return null;
    }

    const onClickReceipt = () => {
      setDownloadState(AxiosStatus.PENDING);
      getReceipt()
        .then(() => {
          setDownloadState(AxiosStatus.SUCCESS);
        })
        .catch((err: IApiResponse) => {
          setErrorMessage(err.description || 'Failed to download receipt!');
          setDownloadState(AxiosStatus.FAILURE);
        });
    };

    return (
      <Button
        onClick={onClickReceipt}
        theme={Themes.secondary}
        className={styles.button}
        disabled={downloadState === AxiosStatus.PENDING}
        fullWidth
      >
        {downloadState === AxiosStatus.PENDING ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          statusMap[status].secondaryBtnText
        )}
      </Button>
    );
  };

  const primaryBtnAction = status === 'success' ? onSuccess || onClose : onRetry;

  const content = (
    <section className={styles.content}>
      <div className={styles.statusInfo}>
        <div className={styles.statusRingOuter}>
          <div className={styles.statusRingInner}>
            <img alt={status} src={statusMap[status].icon} />
          </div>
        </div>
        <p className={styles.message}>{children}</p>
      </div>
      <div>
        {role === Signatory.INITIATOR || Signatory.INITIATOR_AUTHORIZER || Signatory.SINGLE
          ? renderSecondaryButton()
          : null}
        <Button onClick={primaryBtnAction} theme={Themes.primary} className={styles.button} fullWidth>
          {statusMap[status].primaryBtnText}
        </Button>
      </div>
    </section>
  );

  return (
    <ModalWindow open={open} onClose={onClose} fullScreen>
      <>
        {content}
        <Toast
          show={downloadState === AxiosStatus.FAILURE}
          onClose={hideToast}
          severity="error"
          message={errorMessage}
        />
      </>
    </ModalWindow>
  );
};

export default StatusModal;
